// eslint-disable-next-line no-unused-vars
import { useState, useEffect, useRef } from "react";
import TagPicker from "../components/TagPicker";
import FilePreviewModal from "../components/FilePreviewModal";
import FileNotesModal from "../components/FileNotesModal";
import FileThumb from "../components/FileThumb";
import TagFilter from "../components/TagFilter";
import ViewToggle from "../components/ViewToggle";
import { COLORS, API } from "../constants";

export default function FilesView({
  files, selectedFiles, toggleSelectFile, handleDelete, handleBulkDelete,
  handleBulkDownload, handleDownloadAll, fileView, setFileView,
  activeTagFilter, setActiveTagFilter, token,
  folders, activeFolderId, setActiveFolderId,
  showNewCourseForm, setShowNewCourseForm,
  newCourseName, setNewCourseName,
  newCourseSubfolders, setNewCourseSubfolders,
  creatingCourse, handleCreateCourse,
  handleDeleteFolder, handleRenameFolder,
  handleAssignFilesToFolder, handleRemoveFileFromFolder,
  fetchFolderDefaults, fetchFiles,
  sortBy, setSortBy, handleStarFile,
  copiedFileId, handleCopyFile, handlePasteFile,
  showUploadModal, setShowUploadModal, pendingFiles,
  uploadTargetFolder, setUploadTargetFolder, processUpload, handleIncomingFiles,
  showNewFolderInModal, setShowNewFolderInModal,
  newFolderNameInModal, setNewFolderNameInModal,
  duplicateWarning, resolveDuplicate, cancelDuplicateUpload,
  folderSuggestions, loadingSuggestions, setFolderSuggestions,
  handleAcceptSuggestion, handleDismissSuggestion, fetchFolderSuggestions,
  handleUpdateTag, handleSummarizeAll, handleSummarizeFile,
  summarizingFileIds = [], summarizingAll = false, summarizeMessage = "", setSummarizeMessage,
  handleRetagFile, handleRetagAll,
  retaggingFileIds = [], retaggingAll = false, retagMessage = "", setRetagMessage, retagChanges = null, setRetagChanges,
  handleToggleTagLock,
  fetchRelatedFiles, relatedFiles = [], loadingRelated = false, showRelatedPanel = null, setShowRelatedPanel,
  handleBackfillRelationships, backfillingRelationships = false, backfillMessage = "", setBackfillMessage,
  askQuestion = "", setAskQuestion, askAnswer = null, setAskAnswer,
  askLoading = false, askError = "", setAskError, handleAskFiles,
  previewFile = null, setPreviewFile,
  trashedFiles = [], trashedFolders = [], loadingTrash = false, trashMessage = "", setTrashMessage,
  fetchTrash, handleRestoreFile, handlePermanentlyDeleteFile, handleRestoreFolder, handlePermanentlyDeleteFolder,
  selectedTrashFiles = [], selectedTrashFolders = [], toggleSelectTrashFile, toggleSelectTrashFolder,
  handleBulkRestoreFiles, handleBulkPermanentDeleteFiles, handleBulkRestoreFolders, handleBulkPermanentDeleteFolders,
  showStarred, showTrash, goToAllFiles, goToStarred, goToTrash, goToFolder,
  clipboard, pasteCollision, pasteFolderChoice, setPasteFolderChoice, clipboardMessage, setClipboardMessage,
  clipboardCopyFiles, clipboardCutFiles, clipboardCopyFolder, clipboardCutFolder,
  performFilePaste, resolvePasteCollision, initiateFolderPaste, resolveFolderPasteChoice,
  showGapAnalysisModal, gapAnalysisStep, gapAnalysisFiles, gapAnalysisSelectedIds, gapAnalysisCourseFolderName,
  loadingGapAnalysisFiles, gapAnalysisSyllabusFiles, loadingSyllabusFiles, gapAnalysisSyllabusFileId, setGapAnalysisSyllabusFileId,
  gapAnalysisResult, runningGapAnalysis, gapAnalysisError, setGapAnalysisError,
  openGapAnalysisModal, closeGapAnalysisModal, toggleGapAnalysisFile,
  goToGapAnalysisStep2, backToGapAnalysisStep1, runGapAnalysis
}) {

  const [expandedFolders, setExpandedFolders] = useState({});
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renamingFile, setRenamingFile] = useState(null);
  const [renameFileValue, setRenameFileValue] = useState("");
  const [showMovePanel, setShowMovePanel] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [expandedSummaries, setExpandedSummaries] = useState({});
  const [isDraggingFilesOver, setIsDraggingFilesOver] = useState(false);
  const [notesFile, setNotesFile] = useState(null);

  // ── Keyboard navigation (focus + selection) ──
  const [activeZone, setActiveZone] = useState(null); // 'files' | 'folders' | null
  const [focusedFileId, setFocusedFileId] = useState(null);
  const [selectionAnchorId, setSelectionAnchorId] = useState(null);
  const [focusedFolderId, setFocusedFolderId] = useState(null);
  const fileListRef = useRef(null);

  const toggleExpand = (id) => setExpandedFolders(prev => ({ ...prev, [id]: !prev[id] }));

  const findFolder = (folderList, id) => {
    for (const f of folderList) {
      if (f.id === id) return f;
      if (f.children?.length) {
        const found = findFolder(f.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Sort files
  const sortFiles = (fileList) => {
    const sorted = [...fileList];
    switch (sortBy) {
      case "name_asc": return sorted.sort((a, b) => a.filename.localeCompare(b.filename));
      case "name_desc": return sorted.sort((a, b) => b.filename.localeCompare(a.filename));
      case "date_asc": return sorted.sort((a, b) => new Date(a.uploaded_at) - new Date(b.uploaded_at));
      case "date_desc": return sorted.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
      case "type": return sorted.sort((a, b) => a.filename.split(".").pop().localeCompare(b.filename.split(".").pop()));
      default: return sorted;
    }
  };

  const collectAllFileIds = (folder) => {
    const ids = new Set(folder._file_ids || []);
    (folder.children || []).forEach(child => {
      collectAllFileIds(child).forEach(id => ids.add(id));
    });
    return ids;
  };

  const baseFiles = showStarred
    ? files.filter(f => f.is_starred)
    : activeFolderId
      ? (() => {
          const folder = findFolder(folders, activeFolderId);
          if (!folder) return files;
          const ids = collectAllFileIds(folder);
          return files.filter(f => ids.has(f.id));
        })()
      : files;

  const displayFiles = sortFiles(baseFiles);
  const filteredFiles = displayFiles.filter(f => !activeTagFilter || f.tag === activeTagFilter);
  const allFileTags = [...new Set(displayFiles.map(f => f.tag).filter(Boolean))];
  const starredCount = files.filter(f => f.is_starred).length;

  const getBreadcrumb = () => {
    if (showStarred) return [{ id: "starred", name: "Starred" }];
    if (!activeFolderId) return [];
    const crumbs = [];
    const findPath = (folderList, targetId, path) => {
      for (const f of folderList) {
        const newPath = [...path, { id: f.id, name: f.name }];
        if (f.id === targetId) { crumbs.push(...newPath); return true; }
        if (f.children?.length && findPath(f.children, targetId, newPath)) return true;
      }
      return false;
    };
    findPath(folders, activeFolderId, []);
    return crumbs;
  };

  const handleContextMenu = (e, type, item) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, type, item });
  };

  const closeContextMenu = () => setContextMenu(null);

  // ── Drag and Drop ──
  const handleDragStart = (e, fileId) => {
    const dragIds = selectedFiles.includes(fileId) ? selectedFiles : [fileId];
    e.dataTransfer.setData("fileIds", JSON.stringify(dragIds));
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(folderId);
  };

  const handleDrop = (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(null);
    const fileIds = JSON.parse(e.dataTransfer.getData("fileIds") || "[]");
    if (fileIds.length > 0) handleAssignFilesToFolder(folderId, fileIds);
  };

  const handleFileClick = (e, fileId, index) => {
    setActiveZone("files");
    setFocusedFileId(String(fileId));
    setFocusedFolderId(null);
    if (e.shiftKey && lastSelectedIndex !== null) {
      // Range select
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const rangeIds = filteredFiles.slice(start, end + 1).map(f => f.id);
      rangeIds.forEach(id => { if (!selectedFiles.includes(id)) toggleSelectFile(id); });
    } else if (e.ctrlKey || e.metaKey) {
      // Toggle individual
      toggleSelectFile(fileId);
      setLastSelectedIndex(index);
      setSelectionAnchorId(String(fileId));
    } else {
      // Single select — deselect all others first
      selectedFiles.forEach(id => { if (id !== fileId) toggleSelectFile(id); });
      if (!selectedFiles.includes(fileId) || selectedFiles.length > 1) {
        if (!selectedFiles.includes(fileId)) toggleSelectFile(fileId);
      } else {
        toggleSelectFile(fileId);
      }
      setLastSelectedIndex(index);
      setSelectionAnchorId(String(fileId));
    }
  };

  // ── Grid-aware keyboard navigation ──
  // Reads real rendered positions from the DOM so this works correctly regardless
  // of how many columns the responsive tile grid currently has.
  const getFileGridRows = () => {
    const container = fileListRef.current;
    if (!container) return [];
    const nodes = Array.from(container.querySelectorAll("[data-kbd-file-id]"));
    const items = nodes.map(el => ({ id: el.getAttribute("data-kbd-file-id"), top: el.offsetTop, left: el.offsetLeft }));
    const tolerance = 6;
    const rows = [];
    items.sort((a, b) => a.top - b.top || a.left - b.left);
    items.forEach(it => {
      let row = rows.find(r => Math.abs(r.top - it.top) <= tolerance);
      if (!row) { row = { top: it.top, items: [] }; rows.push(row); }
      row.items.push(it);
    });
    rows.forEach(r => r.items.sort((a, b) => a.left - b.left));
    rows.sort((a, b) => a.top - b.top);
    return rows;
  };

  const applySelectionForFocus = (newId, extend, moveOnly) => {
    if (moveOnly) return;
    if (extend) {
      const rows = getFileGridRows();
      const flat = rows.flatMap(r => r.items.map(it => it.id));
      const anchor = selectionAnchorId || focusedFileId || newId;
      const ai = flat.indexOf(String(anchor));
      const ni = flat.indexOf(String(newId));
      if (ai === -1 || ni === -1) return;
      const [start, end] = ai < ni ? [ai, ni] : [ni, ai];
      const rangeIds = flat.slice(start, end + 1).map(id => parseInt(id, 10));
      selectedFiles.forEach(id => { if (!rangeIds.includes(id)) toggleSelectFile(id); });
      rangeIds.forEach(id => { if (!selectedFiles.includes(id)) toggleSelectFile(id); });
    } else {
      const idNum = parseInt(newId, 10);
      selectedFiles.forEach(id => { if (id !== idNum) toggleSelectFile(id); });
      if (!selectedFiles.includes(idNum)) toggleSelectFile(idNum);
      setSelectionAnchorId(String(newId));
    }
  };

  const moveFileFocus = (direction, extend, moveOnly) => {
    const rows = getFileGridRows();
    if (!rows.length) return;
    let curRow = 0, curCol = 0, found = false;
    rows.forEach((r, ri) => r.items.forEach((it, ci) => {
      if (String(it.id) === String(focusedFileId)) { curRow = ri; curCol = ci; found = true; }
    }));
    if (!found) {
      setFocusedFileId(rows[0].items[0].id);
      applySelectionForFocus(rows[0].items[0].id, extend, moveOnly);
      return;
    }
    let newRow = curRow, newCol = curCol;
    if (direction === "up") newRow = Math.max(0, curRow - 1);
    if (direction === "down") newRow = Math.min(rows.length - 1, curRow + 1);
    if (direction === "left") {
      if (curCol > 0) newCol = curCol - 1;
      else if (curRow > 0) { newRow = curRow - 1; newCol = rows[newRow].items.length - 1; }
    }
    if (direction === "right") {
      if (curCol < rows[curRow].items.length - 1) newCol = curCol + 1;
      else if (curRow < rows.length - 1) { newRow = curRow + 1; newCol = 0; }
    }
    newCol = Math.min(newCol, rows[newRow].items.length - 1);
    const newId = rows[newRow].items[newCol].id;
    setFocusedFileId(newId);
    applySelectionForFocus(newId, extend, moveOnly);
  };

  const getVisibleFolderIds = () => {
    const result = [];
    const walk = (list) => {
      list.forEach(f => {
        result.push(f.id);
        if (f.children?.length && expandedFolders[f.id] !== false) walk(f.children);
      });
    };
    walk(folders);
    return result;
  };

  const moveFolderFocus = (direction, moveOnly) => {
    const ids = getVisibleFolderIds();
    if (!ids.length) return;
    let idx = ids.indexOf(focusedFolderId);
    if (idx === -1) idx = 0;
    else idx = direction === "down" ? Math.min(ids.length - 1, idx + 1) : Math.max(0, idx - 1);
    const newId = ids[idx];
    setFocusedFolderId(newId);
    if (!moveOnly) goToFolder(newId);
  };

  const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const deleteKeyName = isMac ? "Backspace" : "Delete";

  const clearFocusAndSelection = () => {
    setActiveZone(null);
    setFocusedFileId(null);
    setFocusedFolderId(null);
    setSelectionAnchorId(null);
    selectedFiles.forEach(id => toggleSelectFile(id));
  };

  useEffect(() => {
    if (!focusedFileId) return;
    const el = fileListRef.current?.querySelector(`[data-kbd-file-id="${focusedFileId}"]`);
    if (el) el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
  }, [focusedFileId]);

  useEffect(() => {
    if (!focusedFolderId) return;
    const el = document.querySelector(`[data-kbd-folder-id="${focusedFolderId}"]`);
    if (el) el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
  }, [focusedFolderId]);;

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable) return;
      if (!activeZone) return;

      if (activeZone === "files") {
        if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
          e.preventDefault();
          const dir = e.key.replace("Arrow", "").toLowerCase();
          moveFileFocus(dir, e.shiftKey, e.ctrlKey || e.metaKey);
        } else if (e.key === "Escape") {
          clearFocusAndSelection();
        } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
          e.preventDefault();
          filteredFiles.forEach(f => { if (!selectedFiles.includes(f.id)) toggleSelectFile(f.id); });
        } else if (e.key === "Enter" && focusedFileId) {
          const f = filteredFiles.find(x => x.id === parseInt(focusedFileId, 10));
          if (f) setPreviewFile({ id: f.id, filename: f.filename });
        } else if (e.key === " " && focusedFileId) {
          e.preventDefault();
          const idNum = parseInt(focusedFileId, 10);
          toggleSelectFile(idNum);
          setSelectionAnchorId(String(focusedFileId));
        } else if (e.key === deleteKeyName) {
          e.preventDefault();
          if (selectedFiles.length > 0) {
            handleBulkDelete(selectedFiles);
          } else if (focusedFileId) {
            const f = filteredFiles.find(x => x.id === parseInt(focusedFileId, 10));
            if (f) handleDelete(f.id, f.filename);
          }
        } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c") {
          e.preventDefault();
          const ids = selectedFiles.length > 0 ? selectedFiles : (focusedFileId ? [parseInt(focusedFileId, 10)] : []);
          if (ids.length) clipboardCopyFiles(ids);
        } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "x") {
          e.preventDefault();
          const ids = selectedFiles.length > 0 ? selectedFiles : (focusedFileId ? [parseInt(focusedFileId, 10)] : []);
          if (ids.length) clipboardCutFiles(ids);
        } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v") {
          e.preventDefault();
          if (clipboard?.type === "file") performFilePaste(activeFolderId || null);
        }
      } else if (activeZone === "folders") {
        if (["ArrowUp", "ArrowDown"].includes(e.key)) {
          e.preventDefault();
          moveFolderFocus(e.key === "ArrowDown" ? "down" : "up", e.ctrlKey || e.metaKey);
        } else if (e.key === "Escape") {
          setActiveZone(null);
          setFocusedFolderId(null);
        } else if (e.key === "Enter" && focusedFolderId) {
          goToFolder(focusedFolderId);
        } else if (e.key === deleteKeyName && focusedFolderId) {
          e.preventDefault();
          handleDeleteFolder(focusedFolderId);
        } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c" && focusedFolderId) {
          e.preventDefault();
          clipboardCopyFolder(focusedFolderId);
        } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "x" && focusedFolderId) {
          e.preventDefault();
          clipboardCutFolder(focusedFolderId);
        } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v") {
          e.preventDefault();
          if (clipboard?.type === "folder") initiateFolderPaste(focusedFolderId || null);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeZone, focusedFileId, focusedFolderId, selectedFiles, filteredFiles, selectionAnchorId, folders, expandedFolders, clipboard, activeFolderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── OS File Drag & Drop (dragging real files in from outside the browser) ──
  const isRealFileDrag = (e) => e.dataTransfer.types.includes("Files");

  const handlePageDragOver = (e) => {
    if (!isRealFileDrag(e)) return;
    e.preventDefault();
    setIsDraggingFilesOver(true);
  };

  const handlePageDragLeave = (e) => {
    if (!isRealFileDrag(e)) return;
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingFilesOver(false);
  };

  const handlePageDrop = (e) => {
    if (!isRealFileDrag(e)) return;
    e.preventDefault();
    setIsDraggingFilesOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) handleIncomingFiles(dropped);
  };

  // ── Folder Tree Node ──
  const FolderNode = ({ folder, depth = 0 }) => {
    const isActive = activeFolderId === folder.id && !showStarred;
    const hasChildren = folder.children?.length > 0;
    const isDragOver = dragOverFolder === folder.id;

    return (
      <div>
        <div
          data-kbd-folder-id={folder.id}
          onClick={() => { setActiveZone("folders"); setFocusedFolderId(folder.id); goToFolder(folder.id); if (hasChildren) toggleExpand(folder.id); }}
          onContextMenu={e => handleContextMenu(e, "folder", folder)}
          onDragOver={e => handleDragOver(e, folder.id)}
          onDrop={e => handleDrop(e, folder.id)}
          onDragLeave={() => setDragOverFolder(null)}
          style={{
            display: "flex", alignItems: "center", gap: "6px",
            padding: `5px 8px 5px ${12 + depth * 16}px`,
            borderRadius: "6px", cursor: "pointer",
            background: isDragOver ? "#dbeafe" : isActive ? COLORS.blueLight : "transparent",
            color: isActive ? COLORS.blue : COLORS.textPrimary,
            fontSize: "13px", userSelect: "none",
            border: isDragOver ? `1.5px dashed ${COLORS.blue}` : "1.5px solid transparent",
            outline: (activeZone === "folders" && focusedFolderId === folder.id) ? `2px dashed ${COLORS.blue}` : "none",
            outlineOffset: "1px",
            transition: "all 0.1s"
          }}
        >
          {hasChildren ? (
            <span style={{ fontSize: "10px", color: COLORS.textTertiary, width: "12px" }}>
              {expandedFolders[folder.id] !== false ? "▾" : "▸"}
            </span>
          ) : <span style={{ width: "12px" }} />}
          <span style={{ fontSize: "14px" }}>{folder.is_private ? "🔒" : "📁"}</span>
          {renamingFolder === folder.id ? (
            <input
              autoFocus value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") { handleRenameFolder(folder.id, renameValue); setRenamingFolder(null); }
                if (e.key === "Escape") setRenamingFolder(null);
              }}
              onBlur={() => setRenamingFolder(null)}
              onClick={e => e.stopPropagation()}
              style={{ flex: 1, fontSize: "12px", padding: "1px 4px", borderRadius: "4px", border: `1px solid ${COLORS.blue}`, outline: "none" }}
            />
          ) : (
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "12px" }}>{folder.name}</span>
          )}
          {folder.file_count > 0 && <span style={{ fontSize: "10px", color: COLORS.textTertiary, flexShrink: 0 }}>{folder.file_count}</span>}
        </div>
        {hasChildren && expandedFolders[folder.id] !== false && (
          <div>
            {folder.children.map(child => <FolderNode key={child.id} folder={child} depth={depth + 1} />)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      onDragOver={handlePageDragOver}
      onDragLeave={handlePageDragLeave}
      onDrop={handlePageDrop}
      style={{ display: "flex", gap: "16px", alignItems: "flex-start", position: "relative" }}
    >
      {isDraggingFilesOver && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(59, 130, 246, 0.1)", border: `3px dashed ${COLORS.blue}`, borderRadius: "12px", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <div style={{ background: COLORS.bg, padding: "24px 40px", borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", textAlign: "center" }}>
            <div style={{ fontSize: "32px", marginBottom: "8px" }}>📤</div>
            <div style={{ fontSize: "15px", fontWeight: "600", color: COLORS.textPrimary }}>Drop files to upload</div>
          </div>
        </div>
      )}

      {/* ── Left Sidebar ── */}
      <div style={{ width: "220px", flexShrink: 0, background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "10px", padding: "12px", minHeight: "400px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
          <span style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary }}>COURSES</span>
          <button onClick={() => { setShowNewCourseForm(true); fetchFolderDefaults(); }} title="New course folder" style={{ padding: "2px 8px", fontSize: "16px", background: "transparent", border: "none", color: COLORS.blue, cursor: "pointer", lineHeight: 1 }}>+</button>
        </div>

        {/* All Files */}
        <div
          onClick={goToAllFiles}
          onDragOver={e => handleDragOver(e, "all")}
          onDrop={e => { e.preventDefault(); setDragOverFolder(null); }}
          onDragLeave={() => setDragOverFolder(null)}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 8px", borderRadius: "6px", cursor: "pointer", background: activeFolderId === null && !showStarred && !showTrash ? COLORS.blueLight : "transparent", marginBottom: "2px" }}
        >
          <span style={{ fontSize: "14px" }}>🗂</span>
          <span style={{ fontSize: "12px", color: activeFolderId === null && !showStarred ? COLORS.blue : COLORS.textPrimary, fontWeight: activeFolderId === null && !showStarred ? "500" : "400" }}>All Files</span>
          <span style={{ fontSize: "10px", color: COLORS.textTertiary, marginLeft: "auto" }}>{files.length}</span>
        </div>

        {/* Starred */}
        <div
          onClick={goToStarred}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 8px", borderRadius: "6px", cursor: "pointer", background: showStarred ? "#FEF9C3" : "transparent", marginBottom: "8px" }}
        >
          <span style={{ fontSize: "14px" }}>⭐</span>
          <span style={{ fontSize: "12px", color: showStarred ? "#713F12" : COLORS.textPrimary, fontWeight: showStarred ? "500" : "400" }}>Starred</span>
          {starredCount > 0 && <span style={{ fontSize: "10px", color: COLORS.textTertiary, marginLeft: "auto" }}>{starredCount}</span>}
        </div>

        {/* Trash */}
        <div
          onClick={goToTrash}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 8px", borderRadius: "6px", cursor: "pointer", background: showTrash ? "#FEE2E2" : "transparent", marginBottom: "8px" }}
        >
          <span style={{ fontSize: "14px" }}>🗑</span>
          <span style={{ fontSize: "12px", color: showTrash ? COLORS.danger : COLORS.textPrimary, fontWeight: showTrash ? "500" : "400" }}>Trash</span>
        </div>

        {/* Divider */}
        <div style={{ height: "0.5px", background: COLORS.borderLight, marginBottom: "8px" }} />
        <div>
          {folders.length === 0 ? (
            <div onClick={() => { setActiveZone("folders"); setFocusedFolderId(null); }} style={{ textAlign: "center", padding: "20px 8px", cursor: "pointer" }}>
              <p style={{ fontSize: "11px", color: COLORS.textTertiary, margin: 0 }}>No courses yet</p>
            </div>
          ) : (
            folders.map(f => <FolderNode key={f.id} folder={f} depth={0} />)
          )}
          <div onClick={() => { setActiveZone("folders"); setFocusedFolderId(null); }} title="Click to paste at root level" style={{ height: "28px", cursor: "pointer" }} />
        </div>
      </div>

      {/* ── Right Panel ── */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {showTrash ? (
          <div>
            <h2 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.textPrimary, marginBottom: "6px" }}>🗑 Trash</h2>
            <p style={{ fontSize: "12px", color: COLORS.textTertiary, marginBottom: "16px" }}>Items are automatically deleted forever after 30 days.</p>

            {trashMessage && (
              <div style={{ marginBottom: "14px", padding: "8px 12px", background: COLORS.dangerBg, border: `0.5px solid ${COLORS.dangerBorder}`, borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: "12px", color: COLORS.danger }}>{trashMessage}</span>
                <span onClick={() => setTrashMessage("")} style={{ cursor: "pointer", fontSize: "13px", color: COLORS.danger }}>✕</span>
              </div>
            )}

            {(selectedTrashFiles.length > 0 || selectedTrashFolders.length > 0) && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "12px", color: COLORS.textTertiary }}>
                  {selectedTrashFolders.length > 0 && `${selectedTrashFolders.length} folder${selectedTrashFolders.length !== 1 ? "s" : ""}`}
                  {selectedTrashFolders.length > 0 && selectedTrashFiles.length > 0 && ", "}
                  {selectedTrashFiles.length > 0 && `${selectedTrashFiles.length} file${selectedTrashFiles.length !== 1 ? "s" : ""}`}
                  {" selected"}
                </span>
                {selectedTrashFiles.length > 0 && (
                  <>
                    <button onClick={handleBulkRestoreFiles} style={{ padding: "4px 10px", fontSize: "12px", background: COLORS.blue, color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}>Restore files</button>
                    <button onClick={handleBulkPermanentDeleteFiles} style={{ padding: "4px 10px", fontSize: "12px", background: COLORS.dangerBg, border: `0.5px solid ${COLORS.dangerBorder}`, borderRadius: "6px", color: COLORS.danger, cursor: "pointer" }}>Delete files forever</button>
                  </>
                )}
                {selectedTrashFolders.length > 0 && (
                  <>
                    <button onClick={handleBulkRestoreFolders} style={{ padding: "4px 10px", fontSize: "12px", background: COLORS.blue, color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}>Restore folders</button>
                    <button onClick={handleBulkPermanentDeleteFolders} style={{ padding: "4px 10px", fontSize: "12px", background: COLORS.dangerBg, border: `0.5px solid ${COLORS.dangerBorder}`, borderRadius: "6px", color: COLORS.danger, cursor: "pointer" }}>Delete folders forever</button>
                  </>
                )}
              </div>
            )}

            {loadingTrash ? (
              <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>Loading trash...</p>
            ) : trashedFiles.length === 0 && trashedFolders.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <p style={{ fontSize: "15px", color: COLORS.textSecondary, marginBottom: "6px" }}>Trash is empty</p>
                <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>Deleted files and folders will appear here</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {trashedFolders.map(f => (
                  <div key={`folder-${f.id}`} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", background: selectedTrashFolders.includes(f.id) ? COLORS.blueLight : COLORS.bg, border: `0.5px solid ${selectedTrashFolders.includes(f.id) ? COLORS.blue : COLORS.border}`, borderRadius: "9px" }}>
                    <div onClick={() => toggleSelectTrashFolder(f.id)} style={{ width: "18px", height: "18px", borderRadius: "4px", border: `1.5px solid ${selectedTrashFolders.includes(f.id) ? COLORS.blue : COLORS.textTertiary}`, background: selectedTrashFolders.includes(f.id) ? COLORS.blue : "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                      {selectedTrashFolders.includes(f.id) && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <span style={{ fontSize: "16px" }}>📁</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "13px", fontWeight: "500", color: COLORS.textPrimary }}>{f.name}</div>
                      <div style={{ fontSize: "11px", color: COLORS.textTertiary }}>Deleted {new Date(f.deleted_at).toLocaleDateString()}</div>
                    </div>
                    <button onClick={() => handleRestoreFolder(f.id)} style={{ padding: "5px 12px", fontSize: "12px", background: COLORS.blue, color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500" }}>Restore</button>
                    <button onClick={() => handlePermanentlyDeleteFolder(f.id, f.name)} style={{ padding: "5px 12px", fontSize: "12px", background: "transparent", border: `0.5px solid ${COLORS.dangerBorder}`, borderRadius: "6px", color: COLORS.danger, cursor: "pointer" }}>Delete Forever</button>
                  </div>
                ))}
                {trashedFiles.map(f => (
                  <div key={`file-${f.id}`} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", background: selectedTrashFiles.includes(f.id) ? COLORS.blueLight : COLORS.bg, border: `0.5px solid ${selectedTrashFiles.includes(f.id) ? COLORS.blue : COLORS.border}`, borderRadius: "9px" }}>
                    <div onClick={() => toggleSelectTrashFile(f.id)} style={{ width: "18px", height: "18px", borderRadius: "4px", border: `1.5px solid ${selectedTrashFiles.includes(f.id) ? COLORS.blue : COLORS.textTertiary}`, background: selectedTrashFiles.includes(f.id) ? COLORS.blue : "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                      {selectedTrashFiles.includes(f.id) && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <FileThumb filename={f.filename} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "13px", fontWeight: "500", color: COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.filename}</div>
                      <div style={{ fontSize: "11px", color: COLORS.textTertiary }}>Deleted {new Date(f.deleted_at).toLocaleDateString()}</div>
                    </div>
                    <button onClick={() => handleRestoreFile(f.id)} style={{ padding: "5px 12px", fontSize: "12px", background: COLORS.blue, color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500" }}>Restore</button>
                    <button onClick={() => handlePermanentlyDeleteFile(f.id, f.filename)} style={{ padding: "5px 12px", fontSize: "12px", background: "transparent", border: `0.5px solid ${COLORS.dangerBorder}`, borderRadius: "6px", color: COLORS.danger, cursor: "pointer" }}>Delete Forever</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
        <>

        {/* New Course Form */}
        {showNewCourseForm && (
          <div style={{ background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "10px", padding: "20px", marginBottom: "16px" }}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: COLORS.textPrimary, marginBottom: "16px" }}>New Course Folder</h3>
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>Course name</label>
              <input type="text" placeholder="e.g. CINE 110 — Introduction to Film" value={newCourseName} onChange={e => setNewCourseName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleCreateCourse()} style={{ width: "100%", padding: "8px 12px", borderRadius: "7px", border: `0.5px solid ${COLORS.border}`, fontSize: "13px", outline: "none", boxSizing: "border-box" }} />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "12px", color: COLORS.textSecondary, display: "block", marginBottom: "8px" }}>Subfolders <span style={{ color: COLORS.textTertiary }}>(uncheck any you don't need)</span></label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {newCourseSubfolders.map((sf, i) => (
                  <div key={i} onClick={() => setNewCourseSubfolders(prev => prev.map((s, j) => j === i ? { ...s, _skip: !s._skip } : s))}
                    style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 12px", borderRadius: "16px", cursor: "pointer", fontSize: "12px", border: `0.5px solid ${sf._skip ? COLORS.border : sf.is_private ? "#D8B4FE" : COLORS.blueMid}`, background: sf._skip ? "transparent" : sf.is_private ? "#F3E8FF" : COLORS.blueLight, color: sf._skip ? COLORS.textTertiary : sf.is_private ? "#581C87" : COLORS.blue }}>
                    {sf.is_private ? "🔒" : "📁"} {sf.name}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => { setShowNewCourseForm(false); setNewCourseName(""); }} style={{ padding: "8px 16px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", color: COLORS.textSecondary, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleCreateCourse} disabled={creatingCourse || !newCourseName.trim()} style={{ padding: "8px 20px", fontSize: "13px", background: creatingCourse || !newCourseName.trim() ? COLORS.bgTertiary : COLORS.blue, color: creatingCourse || !newCourseName.trim() ? COLORS.textTertiary : "white", border: "none", borderRadius: "7px", cursor: creatingCourse || !newCourseName.trim() ? "default" : "pointer", fontWeight: "500" }}>
                {creatingCourse ? "Creating..." : "Create Course"}
              </button>
            </div>
          </div>
        )}

        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px", fontSize: "12px", color: COLORS.textSecondary }}>
          <span onClick={goToAllFiles} style={{ cursor: "pointer", color: (activeFolderId || showStarred || showTrash) ? COLORS.blue : COLORS.textPrimary, fontWeight: (activeFolderId || showStarred || showTrash) ? "400" : "500" }}>My Files</span>
          {getBreadcrumb().map((crumb, i) => (
            <span key={crumb.id} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ color: COLORS.textTertiary }}>›</span>
              <span onClick={() => { if (crumb.id === "starred") { goToStarred(); } else { goToFolder(crumb.id); } }} style={{ cursor: "pointer", color: i === getBreadcrumb().length - 1 ? COLORS.textPrimary : COLORS.blue, fontWeight: i === getBreadcrumb().length - 1 ? "500" : "400" }}>{crumb.name}</span>
            </span>
          ))}
        </div>

        {/* Toolbar */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "12px", color: COLORS.textTertiary, marginRight: "4px" }}>Type</span>
          <TagFilter tags={allFileTags} active={activeTagFilter} onSelect={setActiveTagFilter} />
          {activeFolderId && findFolder(folders, activeFolderId)?.name === "Syllabus" && (
            <button onClick={() => openGapAnalysisModal(activeFolderId)}
              style={{ padding: "5px 12px", fontSize: "12px", background: "#F0FDF4", border: `0.5px solid #86EFAC`, borderRadius: "7px", color: "#166534", cursor: "pointer", fontWeight: "500" }}>
              🔍 Check Coverage
            </button>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "auto" }}>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding: "5px 10px", borderRadius: "6px", border: `0.5px solid ${COLORS.border}`, fontSize: "12px", outline: "none", background: COLORS.bg, color: COLORS.textSecondary, cursor: "pointer" }}>
              <option value="date_desc">Newest first</option>
              <option value="date_asc">Oldest first</option>
              <option value="name_asc">Name A-Z</option>
              <option value="name_desc">Name Z-A</option>
              <option value="type">File type</option>
            </select>
            <ViewToggle value={fileView} onChange={setFileView} />
          </div>
        </div>

        {/* Ask Files */}
        {files.length > 0 && (
          <div style={{ marginBottom: "14px" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="text"
                placeholder={activeFolderId ? "Ask a question about files in this folder..." : "Ask a question about your files..."}
                value={askQuestion}
                onChange={e => setAskQuestion(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !askLoading) handleAskFiles(!!activeFolderId); }}
                style={{ flex: 1, padding: "8px 12px", borderRadius: "8px", border: `0.5px solid ${COLORS.border}`, fontSize: "13px", outline: "none", background: COLORS.bg, color: COLORS.textPrimary }}
              />
              <button onClick={() => handleAskFiles(!!activeFolderId)} disabled={askLoading || !askQuestion.trim()}
                style={{ padding: "8px 16px", fontSize: "13px", background: (askLoading || !askQuestion.trim()) ? COLORS.bgTertiary : COLORS.blue, color: (askLoading || !askQuestion.trim()) ? COLORS.textTertiary : "white", border: "none", borderRadius: "8px", cursor: (askLoading || !askQuestion.trim()) ? "default" : "pointer", fontWeight: "500", whiteSpace: "nowrap" }}>
                {askLoading ? "Asking…" : "🔍 Ask"}
              </button>
            </div>

            {askError && (
              <div style={{ marginTop: "8px", fontSize: "12px", color: COLORS.danger }}>{askError}</div>
            )}

            {askAnswer && (
              <div style={{ marginTop: "10px", background: COLORS.blueLight, border: `0.5px solid ${COLORS.blueMid}`, borderRadius: "10px", padding: "14px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                  <p style={{ fontSize: "13px", color: COLORS.textPrimary, margin: 0, whiteSpace: "pre-wrap", lineHeight: "1.5" }}>{askAnswer.answer}</p>
                  <span onClick={() => setAskAnswer(null)} style={{ cursor: "pointer", fontSize: "14px", color: COLORS.textTertiary, flexShrink: 0 }}>✕</span>
                </div>
                {askAnswer.sources?.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
                    {askAnswer.sources.map(s => (
                      <span key={s.id} onClick={() => setPreviewFile({ id: s.id, filename: s.filename })} style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "12px", background: "white", border: `0.5px solid ${COLORS.blueMid}`, color: COLORS.blue, cursor: "pointer" }}>
                        📄 {s.filename}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Bulk action bar */}
        {files.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "12px", color: COLORS.textTertiary }}>{filteredFiles.length} file{filteredFiles.length !== 1 ? "s" : ""}</span>
            <button onClick={() => {
              if (selectedFiles.length === filteredFiles.length) {
                filteredFiles.forEach(f => selectedFiles.includes(f.id) && toggleSelectFile(f.id));
              } else {
                filteredFiles.forEach(f => !selectedFiles.includes(f.id) && toggleSelectFile(f.id));
              }
            }} style={{ padding: "4px 10px", fontSize: "12px", background: COLORS.blueLight, border: `0.5px solid ${COLORS.blueMid}`, borderRadius: "6px", color: COLORS.blue, cursor: "pointer" }}>
              {selectedFiles.length === filteredFiles.length && filteredFiles.length > 0 ? "Deselect all" : "Select all"}
            </button>
            {!activeFolderId && !showStarred && <button onClick={handleDownloadAll} style={{ padding: "4px 10px", fontSize: "12px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "6px", color: COLORS.textSecondary, cursor: "pointer" }}>Download all</button>}
            <button onClick={handleSummarizeAll} disabled={summarizingAll}
              style={{ padding: "4px 10px", fontSize: "12px", background: summarizingAll ? COLORS.bgTertiary : "#F0FDF4", border: `0.5px solid ${summarizingAll ? COLORS.border : "#86EFAC"}`, borderRadius: "6px", color: summarizingAll ? COLORS.textTertiary : "#166534", cursor: summarizingAll ? "default" : "pointer" }}>
              {summarizingAll ? "✨ Summarizing…" : "✨ Summarize all"}
            </button>
            {summarizeMessage && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: COLORS.textSecondary, background: COLORS.bgSecondary, border: `0.5px solid ${COLORS.border}`, borderRadius: "14px", padding: "4px 8px 4px 12px" }}>
                {summarizeMessage}
                <span onClick={() => setSummarizeMessage("")} style={{ cursor: "pointer", fontSize: "13px", color: COLORS.textTertiary, lineHeight: 1 }}>✕</span>
              </span>
            )}
            <button onClick={handleRetagAll} disabled={retaggingAll}
              style={{ padding: "4px 10px", fontSize: "12px", background: retaggingAll ? COLORS.bgTertiary : "#EFF6FF", border: `0.5px solid ${retaggingAll ? COLORS.border : COLORS.blueMid}`, borderRadius: "6px", color: retaggingAll ? COLORS.textTertiary : COLORS.blue, cursor: retaggingAll ? "default" : "pointer" }}>
              {retaggingAll ? "🏷️ Re-tagging…" : "🏷️ Re-tag all"}
            </button>
            {retagMessage && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: COLORS.textSecondary, background: COLORS.bgSecondary, border: `0.5px solid ${COLORS.border}`, borderRadius: "14px", padding: "4px 8px 4px 12px" }}>
                {retagMessage}
                <span onClick={() => setRetagMessage("")} style={{ cursor: "pointer", fontSize: "13px", color: COLORS.textTertiary, lineHeight: 1 }}>✕</span>
              </span>
            )}
            <button onClick={handleBackfillRelationships} disabled={backfillingRelationships}
              style={{ padding: "4px 10px", fontSize: "12px", background: backfillingRelationships ? COLORS.bgTertiary : "#FEF9F0", border: `0.5px solid ${backfillingRelationships ? COLORS.border : "#FDE68A"}`, borderRadius: "6px", color: backfillingRelationships ? COLORS.textTertiary : "#92400E", cursor: backfillingRelationships ? "default" : "pointer" }}>
              {backfillingRelationships ? "🔗 Scanning…" : "🔗 Scan for relationships"}
            </button>
            {backfillMessage && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: COLORS.textSecondary, background: COLORS.bgSecondary, border: `0.5px solid ${COLORS.border}`, borderRadius: "14px", padding: "4px 8px 4px 12px" }}>
                {backfillMessage}
                <span onClick={() => setBackfillMessage("")} style={{ cursor: "pointer", fontSize: "13px", color: COLORS.textTertiary, lineHeight: 1 }}>✕</span>
              </span>
            )}
            {selectedFiles.length > 0 && <>
              <button onClick={() => handleBulkDownload(files.filter(f => selectedFiles.includes(f.id)))} style={{ padding: "4px 10px", fontSize: "12px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "6px", color: COLORS.textSecondary, cursor: "pointer" }}>Download ({selectedFiles.length})</button>
              <button onClick={e => { e.stopPropagation(); handleBulkDelete(selectedFiles); }} style={{ padding: "4px 10px", fontSize: "12px", background: COLORS.dangerBg, border: `0.5px solid ${COLORS.dangerBorder}`, borderRadius: "6px", color: COLORS.danger, cursor: "pointer" }}>Delete ({selectedFiles.length})</button>
              {activeFolderId && <button onClick={() => { selectedFiles.forEach(id => handleRemoveFileFromFolder(activeFolderId, id)); }} style={{ padding: "4px 10px", fontSize: "12px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "6px", color: COLORS.textSecondary, cursor: "pointer" }}>Remove from folder</button>}
              <button onClick={() => setShowMovePanel(selectedFiles[0])} style={{ padding: "4px 10px", fontSize: "12px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "6px", color: COLORS.textSecondary, cursor: "pointer" }}>Move to folder</button>
            </>}
            {copiedFileId && <span style={{ fontSize: "12px", color: COLORS.blue }}>📋 File copied — right-click a folder to paste</span>}
          </div>
        )}

        {/* Retag results */}
        {retagChanges !== null && (
          <div style={{ background: "#EFF6FF", border: `0.5px solid ${COLORS.blueMid}`, borderRadius: "8px", padding: "12px 14px", marginBottom: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: retagChanges.length ? "8px" : 0 }}>
              <span style={{ fontSize: "13px", fontWeight: "600", color: COLORS.blue }}>
                🏷️ {retagChanges.length === 0 ? "No tags needed updating" : `${retagChanges.length} file${retagChanges.length !== 1 ? "s" : ""} re-tagged`}
              </span>
              <button onClick={() => setRetagChanges(null)} style={{ fontSize: "11px", background: "transparent", border: "none", color: COLORS.blue, cursor: "pointer" }}>Dismiss</button>
            </div>
            {retagChanges.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                {retagChanges.map(c => (
                  <div key={c.file_id} style={{ fontSize: "12px", color: COLORS.textSecondary }}>
                    <span style={{ fontWeight: "500", color: COLORS.textPrimary }}>{c.filename}</span>: {c.old_tag} → {c.new_tag}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Drag hint */}
        {folders.length > 0 && !showStarred && (
          <p style={{ fontSize: "11px", color: COLORS.textTertiary, marginBottom: "10px" }}>💡 Drag files onto folders in the sidebar to organize them</p>
        )}

        {/* AI Suggestions Panel */}
        {folders.length > 0 && !showStarred && !activeFolderId && (
          <div style={{ marginBottom: "16px" }}>
            {folderSuggestions.length === 0 && !loadingSuggestions && (
              <button onClick={fetchFolderSuggestions} style={{ padding: "6px 14px", fontSize: "12px", background: "#F0FDF4", border: `0.5px solid #86EFAC`, borderRadius: "7px", color: "#166534", cursor: "pointer" }}>
                ✨ Get AI folder suggestions
              </button>
            )}
            {loadingSuggestions && (
              <div style={{ padding: "10px 14px", background: COLORS.blueLight, border: `0.5px solid ${COLORS.blueMid}`, borderRadius: "8px", fontSize: "13px", color: COLORS.blue }}>
                ✨ Analyzing your files...
              </div>
            )}
            {folderSuggestions.length > 0 && (
              <div style={{ background: "#F0FDF4", border: `0.5px solid #86EFAC`, borderRadius: "10px", padding: "14px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#166534" }}>✨ AI Folder Suggestions</span>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <button onClick={() => {
                      const allSelected = selectedSuggestions.length === folderSuggestions.length;
                      setSelectedSuggestions(allSelected ? [] : folderSuggestions.map(s => s.file_id));
                    }} style={{ fontSize: "11px", background: "transparent", border: `0.5px solid #86EFAC`, borderRadius: "6px", padding: "3px 10px", color: "#166534", cursor: "pointer" }}>
                      {selectedSuggestions.length === folderSuggestions.length && folderSuggestions.length > 0 ? "Deselect all" : "Select all"}
                    </button>
                    {selectedSuggestions.length > 0 && (
                      <button onClick={() => {
                        folderSuggestions.filter(s => selectedSuggestions.includes(s.file_id)).forEach(s => handleAcceptSuggestion(s));
                        setSelectedSuggestions([]);
                      }} style={{ fontSize: "11px", background: "#166534", color: "white", border: "none", borderRadius: "6px", padding: "3px 10px", cursor: "pointer" }}>
                        Move {selectedSuggestions.length} selected
                      </button>
                    )}
                    <button onClick={() => { setFolderSuggestions([]); setSelectedSuggestions([]); }} style={{ fontSize: "11px", background: "transparent", border: "none", color: "#166534", cursor: "pointer" }}>Dismiss all</button>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {folderSuggestions.map(s => (
                    <div key={s.file_id} onClick={() => setSelectedSuggestions(prev => prev.includes(s.file_id) ? prev.filter(id => id !== s.file_id) : [...prev, s.file_id])}
                      style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", background: selectedSuggestions.includes(s.file_id) ? "#DCFCE7" : "white", borderRadius: "7px", border: `0.5px solid ${selectedSuggestions.includes(s.file_id) ? "#166534" : "#86EFAC"}`, cursor: "pointer" }}>
                      <div style={{ width: "18px", height: "18px", borderRadius: "4px", border: `1.5px solid ${selectedSuggestions.includes(s.file_id) ? "#166534" : "#86EFAC"}`, background: selectedSuggestions.includes(s.file_id) ? "#166534" : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {selectedSuggestions.includes(s.file_id) && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "12px", fontWeight: "500", color: COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.filename}</div>
                        <div style={{ fontSize: "11px", color: COLORS.textSecondary }}>→ {s.course_name} / {s.folder_name} <span style={{ color: COLORS.textTertiary }}>· {s.reason}</span></div>
                      </div>
                      <button onClick={e => { e.stopPropagation(); handleAcceptSuggestion(s); }} style={{ padding: "4px 12px", fontSize: "11px", background: "#166534", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", flexShrink: 0 }}>Move</button>
                      <button onClick={e => { e.stopPropagation(); handleDismissSuggestion(s.file_id); }} style={{ padding: "4px 8px", fontSize: "11px", background: "transparent", border: `0.5px solid #86EFAC`, borderRadius: "6px", color: "#166534", cursor: "pointer", flexShrink: 0 }}>✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {filteredFiles.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            {showStarred ? (
              <>
                <p style={{ fontSize: "15px", color: COLORS.textSecondary, marginBottom: "6px" }}>No starred files yet</p>
                <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>Right-click any file and choose Star to add it here</p>
              </>
            ) : activeFolderId ? (
              <>
                <p style={{ fontSize: "15px", color: COLORS.textSecondary, marginBottom: "6px" }}>This folder is empty</p>
                <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>Drag files here or right-click a file and choose Move to folder</p>
              </>
            ) : (
              <>
                <p style={{ fontSize: "15px", color: COLORS.textSecondary, marginBottom: "6px" }}>No files uploaded yet</p>
                <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>Use the Upload button above to add your course materials</p>
              </>
            )}
          </div>
        )}

        {/* File list */}
        {filteredFiles.length > 0 && (
          <div ref={fileListRef} onClick={e => { if (e.target === e.currentTarget) clearFocusAndSelection(); }}>
            {fileView === "list" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                {filteredFiles.map(f => (
                  <div
                    key={f.id}
                    data-kbd-file-id={f.id}
                    draggable
                    onDragStart={e => handleDragStart(e, f.id)}
                    onContextMenu={e => handleContextMenu(e, "file", f)}
                    onClick={e => handleFileClick(e, f.id, filteredFiles.indexOf(f))}
                    onDoubleClick={() => setNotesFile(f)}
                    style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 12px", background: selectedFiles.includes(f.id) ? COLORS.blueLight : COLORS.bg, border: `0.5px solid ${selectedFiles.includes(f.id) ? COLORS.blue : COLORS.border}`, borderRadius: "8px", cursor: "pointer", outline: (activeZone === "files" && focusedFileId === String(f.id)) ? `2px dashed ${COLORS.blue}` : "none", outlineOffset: "1px" }}
                  >
                    <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}>
                      <div onClick={() => toggleSelectFile(f.id)} style={{ width: "18px", height: "18px", borderRadius: "4px", border: `1.5px solid ${selectedFiles.includes(f.id) ? COLORS.blue : COLORS.textTertiary}`, background: selectedFiles.includes(f.id) ? COLORS.blue : "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                        {selectedFiles.includes(f.id) && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                    </div>
                    <FileThumb filename={f.filename} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {renamingFile === f.id ? (
                        <input
                          autoFocus value={renameFileValue}
                          onChange={e => setRenameFileValue(e.target.value)}
                          onKeyDown={async e => {
                            if (e.key === "Enter") {
                              await fetch(`${API}/files/${f.id}/rename`, {
                                method: "PUT",
                                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                                body: JSON.stringify({ filename: renameFileValue })
                              });
                              setRenamingFile(null);
                              fetchFiles();
                            }
                            if (e.key === "Escape") setRenamingFile(null);
                          }}
                          onBlur={() => setRenamingFile(null)}
                          style={{ width: "100%", fontSize: "13px", padding: "2px 6px", borderRadius: "4px", border: `1px solid ${COLORS.blue}`, outline: "none", boxSizing: "border-box" }}
                        />
                      ) : (
                        <div style={{ fontSize: "13px", color: COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {f.is_starred && <span style={{ marginRight: "4px" }}>⭐</span>}{f.filename}
                        </div>
                      )}
                      {summarizingFileIds.includes(f.id) ? (
                        <div style={{ fontSize: "11px", color: COLORS.blue, marginTop: "2px" }}>✨ Summarizing…</div>
                      ) : f.summary && (
                        <div
                          onClick={e => { e.stopPropagation(); setExpandedSummaries(prev => ({ ...prev, [f.id]: !prev[f.id] })); }}
                          title={expandedSummaries[f.id] ? "Click to collapse" : "Click to read full summary"}
                          style={{
                            fontSize: "11px", color: COLORS.textTertiary, marginTop: "2px", cursor: "pointer", wordBreak: "break-word",
                            ...(expandedSummaries[f.id]
                              ? {}
                              : { overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" })
                          }}
                        >
                          {f.summary}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                      {f.tag_locked && <span title="Tag locked" style={{ fontSize: "12px" }}>🔒</span>}
                      {f.tag && <TagPicker tag={f.tag} fileId={f.id} onUpdate={handleUpdateTag} />}
                      <span onClick={e => { e.stopPropagation(); setPreviewFile({ id: f.id, filename: f.filename }); }} title="Preview" style={{ fontSize: "14px", cursor: "pointer" }}>👁️</span>
                      <span style={{ fontSize: "11px", color: COLORS.textTertiary }}>{new Date(f.uploaded_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {fileView === "tile" && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "10px" }}>
                {filteredFiles.map(f => (
                  <div key={f.id} data-kbd-file-id={f.id} draggable onDragStart={e => handleDragStart(e, f.id)} onContextMenu={e => handleContextMenu(e, "file", f)} onClick={e => handleFileClick(e, f.id, filteredFiles.indexOf(f))} onDoubleClick={() => setNotesFile(f)} style={{ background: selectedFiles.includes(f.id) ? COLORS.blueLight : COLORS.bg, border: `0.5px solid ${selectedFiles.includes(f.id) ? COLORS.blue : COLORS.border}`, borderRadius: "10px", padding: "14px 12px", cursor: "pointer", position: "relative", outline: (activeZone === "files" && focusedFileId === String(f.id)) ? `2px dashed ${COLORS.blue}` : "none", outlineOffset: "1px" }}>
                    {f.is_starred && <span style={{ position: "absolute", top: "6px", right: "6px", fontSize: "12px" }}>⭐</span>}
                    <div style={{ position: "absolute", top: "8px", left: "8px" }}>
                      <div style={{ width: "18px", height: "18px", borderRadius: "4px", border: `1.5px solid ${selectedFiles.includes(f.id) ? COLORS.blue : COLORS.textTertiary}`, background: selectedFiles.includes(f.id) ? COLORS.blue : "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={() => toggleSelectFile(f.id)}>
                        {selectedFiles.includes(f.id) && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: "10px", marginTop: "4px" }}>
                      <div style={{ width: "44px", height: "44px", borderRadius: "10px", background: f.filename.endsWith(".pdf") ? "#FCEBEB" : f.filename.endsWith(".pptx") ? "#FAEEDA" : "#E6F1FB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>
                        {f.filename.endsWith(".pdf") ? "📄" : f.filename.endsWith(".pptx") ? "📊" : "📝"}
                      </div>
                    </div>
                    {renamingFile === f.id ? (
                      <input
                        autoFocus value={renameFileValue}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setRenameFileValue(e.target.value)}
                        onKeyDown={async e => {
                          e.stopPropagation();
                          if (e.key === "Enter") {
                            await fetch(`${API}/files/${f.id}/rename`, {
                              method: "PUT",
                              headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                              body: JSON.stringify({ filename: renameFileValue })
                            });
                            setRenamingFile(null);
                            fetchFiles();
                          }
                          if (e.key === "Escape") setRenamingFile(null);
                        }}
                        onBlur={() => setRenamingFile(null)}
                        style={{ width: "100%", fontSize: "12px", padding: "2px 4px", borderRadius: "4px", border: `1px solid ${COLORS.blue}`, outline: "none", boxSizing: "border-box", textAlign: "center", marginBottom: "8px" }}
                      />
                    ) : (
                      <div style={{ fontSize: "12px", color: COLORS.textPrimary, textAlign: "center", lineHeight: "1.4", marginBottom: "8px", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{f.filename}</div>
                    )}
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap", gap: "4px" }}>
                      {f.tag_locked && <span title="Tag locked" style={{ fontSize: "11px" }}>🔒</span>}
                      {f.tag && <TagPicker tag={f.tag} fileId={f.id} onUpdate={handleUpdateTag} />}
                      <span onClick={e => { e.stopPropagation(); setPreviewFile({ id: f.id, filename: f.filename }); }} title="Preview" style={{ fontSize: "13px", cursor: "pointer" }}>👁️</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {fileView === "compact" && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "6px" }}>
                {filteredFiles.map(f => (
                  <div key={f.id} data-kbd-file-id={f.id} draggable onDragStart={e => handleDragStart(e, f.id)} onContextMenu={e => handleContextMenu(e, "file", f)} onClick={e => handleFileClick(e, f.id, filteredFiles.indexOf(f))} onDoubleClick={() => setNotesFile(f)} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "7px 10px", background: selectedFiles.includes(f.id) ? COLORS.blueLight : COLORS.bg, border: `0.5px solid ${selectedFiles.includes(f.id) ? COLORS.blue : COLORS.border}`, borderRadius: "8px", cursor: "pointer", outline: (activeZone === "files" && focusedFileId === String(f.id)) ? `2px dashed ${COLORS.blue}` : "none", outlineOffset: "1px" }}>
                    <div style={{ width: "18px", height: "18px", borderRadius: "4px", border: `1.5px solid ${selectedFiles.includes(f.id) ? COLORS.blue : COLORS.textTertiary}`, background: selectedFiles.includes(f.id) ? COLORS.blue : "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }} onClick={() => toggleSelectFile(f.id)}>
                      {selectedFiles.includes(f.id) && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <div style={{ width: "26px", height: "26px", borderRadius: "6px", background: f.filename.endsWith(".pdf") ? "#FCEBEB" : f.filename.endsWith(".pptx") ? "#FAEEDA" : "#E6F1FB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", flexShrink: 0 }}>
                      {f.filename.endsWith(".pdf") ? "📄" : f.filename.endsWith(".pptx") ? "📊" : "📝"}
                    </div>
                    {renamingFile === f.id ? (
                      <input
                        autoFocus value={renameFileValue}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setRenameFileValue(e.target.value)}
                        onKeyDown={async e => {
                          e.stopPropagation();
                          if (e.key === "Enter") {
                            await fetch(`${API}/files/${f.id}/rename`, {
                              method: "PUT",
                              headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                              body: JSON.stringify({ filename: renameFileValue })
                            });
                            setRenamingFile(null);
                            fetchFiles();
                          }
                          if (e.key === "Escape") setRenamingFile(null);
                        }}
                        onBlur={() => setRenamingFile(null)}
                        style={{ flex: 1, fontSize: "12px", padding: "2px 4px", borderRadius: "4px", border: `1px solid ${COLORS.blue}`, outline: "none", boxSizing: "border-box" }}
                      />
                    ) : (
                      <span style={{ fontSize: "12px", color: COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{f.is_starred && "⭐ "}{f.filename}</span>
                    )}
                    {f.tag_locked && <span title="Tag locked" style={{ fontSize: "11px" }}>🔒</span>}
                    {f.tag && <TagPicker tag={f.tag} fileId={f.id} onUpdate={handleUpdateTag} />}
                    <span onClick={e => { e.stopPropagation(); setPreviewFile({ id: f.id, filename: f.filename }); }} title="Preview" style={{ fontSize: "12px", cursor: "pointer", flexShrink: 0 }}>👁️</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </>
        )}
      </div>

      {/* ── Context Menu ── */}
      {contextMenu && (
        <>
          <div onClick={closeContextMenu} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
          <div style={{ position: "fixed", top: contextMenu.y, left: contextMenu.x, background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 1000, minWidth: "160px", padding: "4px 0" }} onClick={e => e.stopPropagation()}>
            {contextMenu.type === "folder" && (
              <>
                <div onClick={() => { setRenamingFolder(contextMenu.item.id); setRenameValue(contextMenu.item.name); closeContextMenu(); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>✏️ Rename</div>
                {copiedFileId && <div onClick={() => { handlePasteFile(contextMenu.item.id); closeContextMenu(); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>📋 Paste file here</div>}
                <div onClick={() => { handleDeleteFolder(contextMenu.item.id); closeContextMenu(); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.danger }}>🗑 Delete</div>
              </>
            )}
            {contextMenu.type === "file" && (
              <>
                <div onClick={() => { handleStarFile(contextMenu.item.id); closeContextMenu(); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>
                  {contextMenu.item.is_starred ? "☆ Unstar" : "⭐ Star"}
                </div>
                <div onClick={() => { setPreviewFile({ id: contextMenu.item.id, filename: contextMenu.item.filename }); closeContextMenu(); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>
                  👁️ Preview
                </div>
                <div onClick={() => { setNotesFile(contextMenu.item); closeContextMenu(); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>
                  📝 Notes
                </div>
                <div onClick={() => { if (contextMenu.item.has_extracted_text && !summarizingFileIds.includes(contextMenu.item.id)) { handleSummarizeFile(contextMenu.item.id); closeContextMenu(); } }}
                  title={contextMenu.item.has_extracted_text ? "" : "Not enough extractable text in this file"}
                  style={{ padding: "8px 16px", fontSize: "13px", cursor: (contextMenu.item.has_extracted_text && !summarizingFileIds.includes(contextMenu.item.id)) ? "pointer" : "default", color: (contextMenu.item.has_extracted_text && !summarizingFileIds.includes(contextMenu.item.id)) ? COLORS.textPrimary : COLORS.textTertiary }}>
                  ✨ {summarizingFileIds.includes(contextMenu.item.id) ? "Summarizing…" : contextMenu.item.summary ? "Re-summarize" : "Summarize"}
                </div>
                <div onClick={() => { if (contextMenu.item.has_extracted_text && !retaggingFileIds.includes(contextMenu.item.id)) { handleRetagFile(contextMenu.item.id, contextMenu.item.filename); closeContextMenu(); } }}
                  title={contextMenu.item.has_extracted_text ? "" : "Not enough extractable text in this file"}
                  style={{ padding: "8px 16px", fontSize: "13px", cursor: (contextMenu.item.has_extracted_text && !retaggingFileIds.includes(contextMenu.item.id)) ? "pointer" : "default", color: (contextMenu.item.has_extracted_text && !retaggingFileIds.includes(contextMenu.item.id)) ? COLORS.textPrimary : COLORS.textTertiary }}>
                  🏷️ {retaggingFileIds.includes(contextMenu.item.id) ? "Re-tagging…" : "Re-tag"}
                </div>
                <div onClick={() => { handleToggleTagLock(contextMenu.item.id); closeContextMenu(); }}
                  style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>
                  {contextMenu.item.tag_locked ? "🔓 Unlock tag" : "🔒 Lock tag"}
                </div>
                <div onClick={() => { fetchRelatedFiles(contextMenu.item.id); closeContextMenu(); }}
                  style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>
                  🔗 Related files
                </div>
                <div style={{ borderTop: `0.5px solid ${COLORS.borderLight}`, margin: "4px 0" }} />
                <div onClick={() => { window.open(`${API}/files/${contextMenu.item.id}/download?token=${token}`); closeContextMenu(); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>⬇️ Download</div>
                <div onClick={() => { setRenamingFile(contextMenu.item.id); setRenameFileValue(contextMenu.item.filename); closeContextMenu(); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>✏️ Rename</div>
                <div onClick={() => { handleCopyFile(contextMenu.item.id); closeContextMenu(); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>📋 Copy</div>
                <div onClick={() => { setShowMovePanel(contextMenu.item.id); closeContextMenu(); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>📁 Move to folder</div>
                {activeFolderId && <div onClick={() => { handleRemoveFileFromFolder(activeFolderId, contextMenu.item.id); closeContextMenu(); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>↩️ Remove from folder</div>}
                <div style={{ borderTop: `0.5px solid ${COLORS.borderLight}`, margin: "4px 0" }} />
                <div onClick={() => { closeContextMenu(); setTimeout(() => handleDelete(contextMenu.item.id, contextMenu.item.filename), 10); }} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.danger }}>🗑 Delete</div>
              </>
            )}
          </div>
        </>
      )}

      {/* ── Move to Folder Panel ── */}
      {showMovePanel && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowMovePanel(null)}>
          <div style={{ background: COLORS.bg, borderRadius: "12px", padding: "24px", minWidth: "300px", maxWidth: "400px", boxShadow: "0 8px 32px rgba(0,0,0,0.16)" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: "15px", fontWeight: "600", color: COLORS.textPrimary, marginBottom: "16px" }}>
              Move to folder {selectedFiles.length > 1 ? `(${selectedFiles.length} files)` : ""}
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", maxHeight: "300px", overflowY: "auto" }}>
              {folders.map(course => (
                <div key={course.id}>
                  <div style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, padding: "6px 8px", background: COLORS.bgSecondary, borderRadius: "6px", marginBottom: "2px" }}>📁 {course.name}</div>
                  {course.children?.map(sub => (
                    <div key={sub.id} onClick={() => {
                      const ids = selectedFiles.length > 1 ? selectedFiles : [showMovePanel];
                      handleAssignFilesToFolder(sub.id, ids);
                      setShowMovePanel(null);
                    }} style={{ padding: "7px 12px 7px 24px", fontSize: "13px", cursor: "pointer", borderRadius: "6px", color: COLORS.textPrimary, display: "flex", alignItems: "center", gap: "6px" }}>
                      {sub.is_private ? "🔒" : "📁"} {sub.name}
                    </div>
                  ))}
                  <div onClick={() => {
                    const ids = selectedFiles.length > 1 ? selectedFiles : [showMovePanel];
                    handleAssignFilesToFolder(course.id, ids);
                    setShowMovePanel(null);
                  }} style={{ padding: "7px 12px", fontSize: "13px", cursor: "pointer", borderRadius: "6px", color: COLORS.textSecondary, fontStyle: "italic" }}>
                    + Add to {course.name} (root)
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setShowMovePanel(null)} style={{ marginTop: "16px", width: "100%", padding: "8px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", fontSize: "13px", cursor: "pointer", color: COLORS.textSecondary }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Related Files Panel ── */}
      {showRelatedPanel && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowRelatedPanel(null)}>
          <div style={{ background: COLORS.bg, borderRadius: "12px", padding: "24px", minWidth: "320px", maxWidth: "440px", boxShadow: "0 8px 32px rgba(0,0,0,0.16)" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: "15px", fontWeight: "600", color: COLORS.textPrimary, marginBottom: "16px" }}>🔗 Related Files</h3>
            {loadingRelated ? (
              <p style={{ fontSize: "13px", color: COLORS.textSecondary }}>Looking for related files...</p>
            ) : relatedFiles.length === 0 ? (
              <div>
                <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "6px" }}>No related files found yet.</p>
                <p style={{ fontSize: "12px", color: COLORS.textTertiary }}>Relationships are computed automatically on upload. If this file was uploaded before that feature existed, try "🔗 Scan for relationships" in the toolbar.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "320px", overflowY: "auto" }}>
                {relatedFiles.map(rf => (
                  <div key={rf.id} onClick={() => { setPreviewFile({ id: rf.id, filename: rf.filename }); setShowRelatedPanel(null); }}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: "7px", border: `0.5px solid ${COLORS.border}`, cursor: "pointer" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: "13px", color: COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rf.filename}</div>
                      <div style={{ fontSize: "11px", color: COLORS.textTertiary }}>{rf.tag} · {Math.round(rf.similarity * 100)}% similar</div>
                    </div>
                  </div>
                ))}

              </div>
            )}
            <button onClick={() => setShowRelatedPanel(null)} style={{ marginTop: "16px", width: "100%", padding: "8px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", fontSize: "13px", cursor: "pointer", color: COLORS.textSecondary }}>Close</button>
          </div>
        </div>
      )}

      {/* ── Upload Modal ── */}
      {showUploadModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowUploadModal(false)}>
          <div style={{ background: COLORS.bg, borderRadius: "14px", padding: "28px", minWidth: "380px", maxWidth: "480px", width: "90%", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ marginBottom: "20px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.textPrimary, margin: "0 0 4px 0" }}>
                Upload {pendingFiles.length} file{pendingFiles.length !== 1 ? "s" : ""}
              </h3>
              <p style={{ fontSize: "12px", color: COLORS.textTertiary, margin: 0 }}>
                Choose where to place {pendingFiles.length === 1 ? `"${pendingFiles[0]?.name}"` : "these files"}
              </p>
            </div>

            {/* Folder Tree */}
            <div style={{ maxHeight: "280px", overflowY: "auto", marginBottom: "16px", border: `0.5px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px" }}>
              {/* No folder option */}
              <div onClick={() => setUploadTargetFolder(null)}
                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", background: uploadTargetFolder === null ? COLORS.blueLight : "transparent", marginBottom: "4px" }}>
                <span style={{ fontSize: "16px" }}>🗂</span>
                <span style={{ fontSize: "13px", color: uploadTargetFolder === null ? COLORS.blue : COLORS.textPrimary, fontWeight: uploadTargetFolder === null ? "500" : "400" }}>My Files (no folder)</span>
                {uploadTargetFolder === null && <span style={{ marginLeft: "auto", fontSize: "16px", color: COLORS.blue }}>✓</span>}
              </div>

              {/* Course folders */}
              {folders.map(course => (
                <div key={course.id} style={{ marginBottom: "4px" }}>
                  {/* Course header */}
                  <div onClick={() => setUploadTargetFolder(course.id)}
                    style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", background: uploadTargetFolder === course.id ? COLORS.blueLight : COLORS.bgSecondary }}>
                    <span style={{ fontSize: "16px" }}>📁</span>
                    <span style={{ fontSize: "13px", fontWeight: "500", color: uploadTargetFolder === course.id ? COLORS.blue : COLORS.textPrimary, flex: 1 }}>{course.name}</span>
                    {uploadTargetFolder === course.id && <span style={{ fontSize: "16px", color: COLORS.blue }}>✓</span>}
                  </div>
                  {/* Subfolders */}
                  {course.children?.map(sub => (
                    <div key={sub.id} onClick={() => setUploadTargetFolder(sub.id)}
                      style={{ display: "flex", alignItems: "center", gap: "8px", padding: "7px 10px 7px 28px", borderRadius: "6px", cursor: "pointer", background: uploadTargetFolder === sub.id ? COLORS.blueLight : "transparent" }}>
                      <span style={{ fontSize: "14px" }}>{sub.is_private ? "🔒" : "📁"}</span>
                      <span style={{ fontSize: "12px", color: uploadTargetFolder === sub.id ? COLORS.blue : COLORS.textSecondary, flex: 1 }}>{sub.name}</span>
                      {uploadTargetFolder === sub.id && <span style={{ fontSize: "16px", color: COLORS.blue }}>✓</span>}
                    </div>
                  ))}
                </div>
              ))}

              {/* Create new folder option */}
              {!showNewFolderInModal ? (
                <div onClick={() => setShowNewFolderInModal(true)}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", color: COLORS.blue, marginTop: "4px" }}>
                  <span style={{ fontSize: "16px" }}>➕</span>
                  <span style={{ fontSize: "13px" }}>Create new course folder</span>
                </div>
              ) : (
                <div style={{ padding: "8px 10px", marginTop: "4px" }}>
                  <input
                    autoFocus
                    type="text"
                    placeholder="Course name e.g. CINE 110"
                    value={newFolderNameInModal}
                    onChange={e => setNewFolderNameInModal(e.target.value)}
                    onKeyDown={async e => {
                      if (e.key === "Enter" && newFolderNameInModal.trim()) {
                        const res = await fetch(`${API}/folders/course`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                          body: JSON.stringify({ name: newFolderNameInModal, subfolders: [
                            { name: "Syllabus", is_private: false },
                            { name: "Lectures", is_private: false },
                            { name: "Presentations", is_private: false },
                            { name: "Quizzes", is_private: false },
                            { name: "Assignments", is_private: false },
                            { name: "Exams", is_private: false },
                            { name: "Projects", is_private: false },
                            { name: "Grades", is_private: true },
                            { name: "Answer Keys", is_private: true },
                          ]})
                        });
                        const data = await res.json();
                        setUploadTargetFolder(data.id);
                        setShowNewFolderInModal(false);
                        setNewFolderNameInModal("");
                        await fetch(`${API}/folders`, { headers: { Authorization: `Bearer ${token}` } });
                        // folders will refresh after upload
                      }
                      if (e.key === "Escape") { setShowNewFolderInModal(false); setNewFolderNameInModal(""); }
                    }}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: "7px", border: `1px solid ${COLORS.blue}`, fontSize: "13px", outline: "none", boxSizing: "border-box" }}
                  />
                  <p style={{ fontSize: "11px", color: COLORS.textTertiary, margin: "4px 0 0 0" }}>Press Enter to create · Esc to cancel</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => { setShowUploadModal(false); setShowNewFolderInModal(false); setNewFolderNameInModal(""); }}
                style={{ padding: "9px 18px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "8px", color: COLORS.textSecondary, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={() => processUpload(pendingFiles, uploadTargetFolder)}
                style={{ padding: "9px 24px", fontSize: "13px", background: COLORS.blue, color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "500" }}>
                Upload {uploadTargetFolder ? "to folder" : "without folder"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Duplicate Warning Modal ── */}
      {duplicateWarning && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1001, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: COLORS.bg, borderRadius: "14px", padding: "24px", minWidth: "360px", maxWidth: "440px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
            <h3 style={{ fontSize: "15px", fontWeight: "600", color: COLORS.textPrimary, marginBottom: "10px" }}>⚠️ This file looks similar to an existing one</h3>
            <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "16px" }}>
              <span style={{ fontWeight: "500", color: COLORS.textPrimary }}>{duplicateWarning.incomingFile?.name}</span> is {Math.round((duplicateWarning.existingFile?.similarity || 0) * 100)}% similar to <span style={{ fontWeight: "500", color: COLORS.textPrimary }}>{duplicateWarning.existingFile?.filename}</span>, already in your files.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <button onClick={() => resolveDuplicate("keepBoth")}
                style={{ padding: "9px 14px", fontSize: "13px", background: COLORS.blue, color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "500", textAlign: "left" }}>
                Keep both — upload as a new file
              </button>
              <button onClick={() => resolveDuplicate("replace")}
                style={{ padding: "9px 14px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "8px", color: COLORS.textPrimary, cursor: "pointer", textAlign: "left" }}>
                Replace — delete "{duplicateWarning.existingFile?.filename}" and upload this instead
              </button>
              <button onClick={cancelDuplicateUpload}
                style={{ padding: "9px 14px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "8px", color: COLORS.textSecondary, cursor: "pointer", textAlign: "left" }}>
                Skip this file
              </button>
            </div>
          </div>
        </div>
      )}

      {clipboardMessage && (
        <div style={{ position: "fixed", bottom: "20px", left: "50%", transform: "translateX(-50%)", zIndex: 1100, background: COLORS.textPrimary, color: "white", padding: "8px 16px", borderRadius: "8px", fontSize: "13px", display: "flex", alignItems: "center", gap: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}>
          {clipboardMessage}
          <span onClick={() => setClipboardMessage("")} style={{ cursor: "pointer", opacity: 0.7 }}>✕</span>
        </div>
      )}

      {/* ── Paste Collision Dialog ── */}
      {pasteCollision && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1101, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: COLORS.bg, borderRadius: "14px", padding: "24px", minWidth: "360px", maxWidth: "440px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
            <h3 style={{ fontSize: "15px", fontWeight: "600", color: COLORS.textPrimary, marginBottom: "10px" }}>A file named "{pasteCollision.filename}" already exists here</h3>
            <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "16px" }}>What would you like to do?</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <button onClick={() => resolvePasteCollision("overwrite")}
                style={{ padding: "9px 14px", fontSize: "13px", background: COLORS.blue, color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "500", textAlign: "left" }}>
                Overwrite — replace the existing file
              </button>
              <button onClick={() => resolvePasteCollision("duplicate")}
                style={{ padding: "9px 14px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "8px", color: COLORS.textPrimary, cursor: "pointer", textAlign: "left" }}>
                Keep both — save as "{pasteCollision.filename} (1)"
              </button>
              <button onClick={() => resolvePasteCollision("cancel")}
                style={{ padding: "9px 14px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "8px", color: COLORS.textSecondary, cursor: "pointer", textAlign: "left" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Folder Paste Choice Dialog ── */}
      {pasteFolderChoice && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1101, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: COLORS.bg, borderRadius: "14px", padding: "24px", minWidth: "360px", maxWidth: "440px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
            <h3 style={{ fontSize: "15px", fontWeight: "600", color: COLORS.textPrimary, marginBottom: "10px" }}>Copy this folder</h3>
            <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "16px" }}>Do you want to copy everything inside it too, or just create an empty folder with the same structure?</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <button onClick={() => resolveFolderPasteChoice(true)}
                style={{ padding: "9px 14px", fontSize: "13px", background: COLORS.blue, color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "500", textAlign: "left" }}>
                Copy with all contents
              </button>
              <button onClick={() => resolveFolderPasteChoice(false)}
                style={{ padding: "9px 14px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "8px", color: COLORS.textPrimary, cursor: "pointer", textAlign: "left" }}>
                Empty folder only
              </button>
              <button onClick={() => setPasteFolderChoice(null)}
                style={{ padding: "9px 14px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "8px", color: COLORS.textSecondary, cursor: "pointer", textAlign: "left" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Learning Objective Gap Analysis Modal ── */}
      {showGapAnalysisModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1101, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeGapAnalysisModal}>
          <div style={{ background: COLORS.bg, borderRadius: "14px", padding: "24px", minWidth: "420px", maxWidth: "560px", width: "90%", maxHeight: "80vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.textPrimary, margin: 0 }}>🔍 Check Learning Objective Coverage</h3>
              <span onClick={closeGapAnalysisModal} style={{ cursor: "pointer", fontSize: "16px", color: COLORS.textTertiary }}>✕</span>
            </div>
            {gapAnalysisCourseFolderName && (
              <p style={{ fontSize: "12px", color: COLORS.textTertiary, marginBottom: "16px" }}>Course: {gapAnalysisCourseFolderName}</p>
            )}

            {gapAnalysisError && (
              <div style={{ marginBottom: "14px", padding: "8px 12px", background: COLORS.dangerBg, border: `0.5px solid ${COLORS.dangerBorder}`, borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: "12px", color: COLORS.danger }}>{gapAnalysisError}</span>
                <span onClick={() => setGapAnalysisError("")} style={{ cursor: "pointer", fontSize: "13px", color: COLORS.danger }}>✕</span>
              </div>
            )}

            {gapAnalysisStep === 1 && (
              <>
                <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "10px" }}>
                  Step 1 of 2: Select which files to check for learning-objective coverage — uncheck any that belong to a different term.
                </p>
                {loadingGapAnalysisFiles ? (
                  <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>Loading files...</p>
                ) : gapAnalysisFiles.length === 0 ? (
                  <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>No files found in this course folder.</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", maxHeight: "280px", overflowY: "auto", marginBottom: "16px" }}>
                    {gapAnalysisFiles.map(f => (
                      <div key={f.id} onClick={() => toggleGapAnalysisFile(f.id)}
                        style={{ display: "flex", alignItems: "center", gap: "10px", padding: "7px 10px", borderRadius: "7px", cursor: "pointer", background: gapAnalysisSelectedIds.includes(f.id) ? COLORS.blueLight : "transparent" }}>
                        <div style={{ width: "16px", height: "16px", borderRadius: "4px", border: `1.5px solid ${gapAnalysisSelectedIds.includes(f.id) ? COLORS.blue : COLORS.textTertiary}`, background: gapAnalysisSelectedIds.includes(f.id) ? COLORS.blue : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {gapAnalysisSelectedIds.includes(f.id) && <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </div>
                        <span style={{ fontSize: "13px", color: COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.filename}</span>
                        <span style={{ fontSize: "11px", color: COLORS.textTertiary, marginLeft: "auto", flexShrink: 0 }}>{f.tag}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                  <button onClick={closeGapAnalysisModal} style={{ padding: "8px 16px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", color: COLORS.textSecondary, cursor: "pointer" }}>Cancel</button>
                  <button onClick={goToGapAnalysisStep2} disabled={loadingSyllabusFiles || gapAnalysisSelectedIds.length === 0}
                    style={{ padding: "8px 20px", fontSize: "13px", background: loadingSyllabusFiles || gapAnalysisSelectedIds.length === 0 ? COLORS.bgTertiary : "#166534", color: loadingSyllabusFiles || gapAnalysisSelectedIds.length === 0 ? COLORS.textTertiary : "white", border: "none", borderRadius: "7px", cursor: loadingSyllabusFiles || gapAnalysisSelectedIds.length === 0 ? "default" : "pointer", fontWeight: "500" }}>
                    {loadingSyllabusFiles ? "Loading..." : "Next →"}
                  </button>
                </div>
              </>
            )}

            {gapAnalysisStep === 2 && (
              <>
                <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "10px" }}>
                  Step 2 of 2: Choose which file in the Syllabus folder to extract learning objectives from.
                </p>
                {gapAnalysisSyllabusFiles.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "24px 10px", marginBottom: "16px" }}>
                    <span style={{ fontSize: "28px", display: "block", marginBottom: "8px" }}>📋</span>
                    <p style={{ fontSize: "13px", color: COLORS.textSecondary, margin: 0 }}>No files found in the Syllabus subfolder — upload your syllabus there first.</p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", maxHeight: "280px", overflowY: "auto", marginBottom: "16px" }}>
                    {gapAnalysisSyllabusFiles.map(f => (
                      <div key={f.id} onClick={() => setGapAnalysisSyllabusFileId(f.id)}
                        style={{ display: "flex", alignItems: "center", gap: "10px", padding: "7px 10px", borderRadius: "7px", cursor: "pointer", background: gapAnalysisSyllabusFileId === f.id ? COLORS.blueLight : "transparent" }}>
                        <div style={{ width: "16px", height: "16px", borderRadius: "50%", border: `1.5px solid ${gapAnalysisSyllabusFileId === f.id ? COLORS.blue : COLORS.textTertiary}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {gapAnalysisSyllabusFileId === f.id && <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: COLORS.blue }} />}
                        </div>
                        <span style={{ fontSize: "13px", color: COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.filename}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                  <button onClick={backToGapAnalysisStep1} style={{ padding: "8px 16px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", color: COLORS.textSecondary, cursor: "pointer" }}>← Back</button>
                  <button onClick={runGapAnalysis} disabled={runningGapAnalysis || !gapAnalysisSyllabusFileId}
                    style={{ padding: "8px 20px", fontSize: "13px", background: runningGapAnalysis || !gapAnalysisSyllabusFileId ? COLORS.bgTertiary : "#166534", color: runningGapAnalysis || !gapAnalysisSyllabusFileId ? COLORS.textTertiary : "white", border: "none", borderRadius: "7px", cursor: runningGapAnalysis || !gapAnalysisSyllabusFileId ? "default" : "pointer", fontWeight: "500" }}>
                    {runningGapAnalysis ? "Analyzing..." : "Run Analysis"}
                  </button>
                </div>
              </>
            )}

            {gapAnalysisStep === 3 && gapAnalysisResult && gapAnalysisResult.status !== "ok" && (
              <div style={{ textAlign: "center", padding: "30px 10px" }}>
                <span style={{ fontSize: "32px", display: "block", marginBottom: "10px" }}>📋</span>
                <p style={{ fontSize: "13px", color: COLORS.textSecondary, margin: 0 }}>{gapAnalysisResult.message}</p>
                <button onClick={closeGapAnalysisModal} style={{ marginTop: "16px", padding: "9px 20px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", fontSize: "13px", cursor: "pointer", color: COLORS.textSecondary }}>Close</button>
              </div>
            )}

            {gapAnalysisStep === 3 && gapAnalysisResult?.status === "ok" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {gapAnalysisResult.objectives.map((obj, i) => {
                  const style = obj.status === "covered"
                    ? { icon: "✅", bg: "#F0FDF4", border: "#86EFAC", color: "#166534" }
                    : obj.status === "partial"
                    ? { icon: "⚠️", bg: "#FEF9F0", border: "#FDE68A", color: "#92400E" }
                    : { icon: "❌", bg: COLORS.dangerBg, border: COLORS.dangerBorder, color: COLORS.danger };
                  return (
                    <div key={i} style={{ padding: "12px 14px", background: style.bg, border: `0.5px solid ${style.border}`, borderRadius: "9px" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "4px" }}>
                        <span style={{ fontSize: "14px", flexShrink: 0 }}>{style.icon}</span>
                        <span style={{ fontSize: "13px", fontWeight: "500", color: COLORS.textPrimary }}>{obj.objective}</span>
                      </div>
                      <p style={{ fontSize: "12px", color: style.color, margin: "0 0 0 22px" }}>{obj.reason}</p>
                      {obj.supporting_files?.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "6px", marginLeft: "22px" }}>
                          {obj.supporting_files.map((fn, j) => (
                            <span key={j} style={{ fontSize: "11px", padding: "2px 8px", borderRadius: "10px", background: "white", border: `0.5px solid ${style.border}`, color: style.color }}>📄 {fn}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <button onClick={closeGapAnalysisModal} style={{ marginTop: "8px", padding: "9px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", fontSize: "13px", cursor: "pointer", color: COLORS.textSecondary }}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {notesFile && (
        <FileNotesModal file={notesFile} onClose={() => setNotesFile(null)} token={token} setPreviewFile={setPreviewFile} />
      )}

      <FilePreviewModal previewFile={previewFile} setPreviewFile={setPreviewFile} token={token} />

    </div>
  );
}